import { Plus, Trash2, ChevronUp, ChevronDown } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";

function BlockCard({ title, enabled, onToggle, children, testid }) {
  return (
    <div className="border border-neutral-200 rounded-lg bg-white" data-testid={testid}>
      <div className="flex items-center justify-between px-5 py-3 border-b border-neutral-100">
        <h3 className="font-semibold text-sm">{title}</h3>
        <div className="flex items-center gap-2">
          <span className="text-xs text-neutral-500">{enabled ? "показан" : "скрыт"}</span>
          <Switch checked={enabled} onCheckedChange={onToggle} data-testid={`${testid}-toggle`} />
        </div>
      </div>
      <div className={`p-5 space-y-4 ${enabled ? "" : "opacity-50 pointer-events-none"}`}>{children}</div>
    </div>
  );
}

function ItemsEditor({ items, onChange, testid, textLabel = "Описание" }) {
  const upd = (i, k, v) => onChange(items.map((it, idx) => (idx === i ? { ...it, [k]: v } : it)));
  const add = () => onChange([...items, { title: "", text: "" }]);
  const remove = (i) => onChange(items.filter((_, idx) => idx !== i));
  const move = (i, d) => {
    const j = i + d;
    if (j < 0 || j >= items.length) return;
    const next = [...items];
    [next[i], next[j]] = [next[j], next[i]];
    onChange(next);
  };
  return (
    <div className="space-y-3">
      {items.map((it, i) => (
        <div key={i} className="border border-neutral-200 rounded-md p-3 space-y-2" data-testid={`${testid}-item-${i}`}>
          <div className="flex items-center gap-2">
            <Input value={it.title} onChange={(e) => upd(i, "title", e.target.value)} placeholder="Заголовок" className="border-neutral-300" data-testid={`${testid}-title-${i}`} />
            <div className="flex flex-col">
              <button type="button" onClick={() => move(i, -1)} className="text-neutral-400 hover:text-black"><ChevronUp className="w-4 h-4" /></button>
              <button type="button" onClick={() => move(i, 1)} className="text-neutral-400 hover:text-black"><ChevronDown className="w-4 h-4" /></button>
            </div>
            <button type="button" onClick={() => remove(i)} className="text-red-600 p-1" data-testid={`${testid}-remove-${i}`}><Trash2 className="w-4 h-4" /></button>
          </div>
          <Textarea value={it.text} onChange={(e) => upd(i, "text", e.target.value)} placeholder={textLabel} rows={2} className="border-neutral-300 text-sm" data-testid={`${testid}-text-${i}`} />
        </div>
      ))}
      <Button type="button" variant="outline" size="sm" onClick={add} data-testid={`${testid}-add`}>
        <Plus className="w-3.5 h-3.5 mr-1" /> Добавить
      </Button>
    </div>
  );
}

export default function HomeEditor({ home, onChange }) {
  const h = home || {};
  const setBlock = (block, patch) => onChange({ ...h, [block]: { ...(h[block] || {}), ...patch } });

  const hero = h.hero || {};
  const how = h.how || {};
  const features = h.features || {};
  const stats = h.stats || {};

  return (
    <div className="space-y-6">
      <BlockCard title="Hero (главный блок)" enabled={hero.enabled !== false} onToggle={(v) => setBlock("hero", { enabled: v })} testid="home-hero">
        <div className="space-y-1.5">
          <Label>Заголовок</Label>
          <Input value={hero.title || ""} onChange={(e) => setBlock("hero", { title: e.target.value })} className="border-neutral-300" data-testid="home-hero-title" />
        </div>
        <div className="space-y-1.5">
          <Label>Подзаголовок</Label>
          <Textarea value={hero.subtitle || ""} onChange={(e) => setBlock("hero", { subtitle: e.target.value })} rows={2} className="border-neutral-300" data-testid="home-hero-subtitle" />
        </div>
        <p className="text-xs text-neutral-400">Форма проверки VIN/кода отображается в этом блоке автоматически.</p>
      </BlockCard>

      <BlockCard title="Как это работает" enabled={how.enabled !== false} onToggle={(v) => setBlock("how", { enabled: v })} testid="home-how">
        <div className="space-y-1.5">
          <Label>Заголовок блока</Label>
          <Input value={how.title || ""} onChange={(e) => setBlock("how", { title: e.target.value })} className="border-neutral-300" data-testid="home-how-title" />
        </div>
        <div className="space-y-1.5">
          <Label>Шаги</Label>
          <ItemsEditor items={how.steps || []} onChange={(v) => setBlock("how", { steps: v })} testid="home-how" />
        </div>
      </BlockCard>

      <BlockCard title="Что можно проверить" enabled={features.enabled !== false} onToggle={(v) => setBlock("features", { enabled: v })} testid="home-features">
        <div className="space-y-1.5">
          <Label>Заголовок блока</Label>
          <Input value={features.title || ""} onChange={(e) => setBlock("features", { title: e.target.value })} className="border-neutral-300" data-testid="home-features-title" />
        </div>
        <div className="space-y-1.5">
          <Label>Карточки</Label>
          <ItemsEditor items={features.cards || []} onChange={(v) => setBlock("features", { cards: v })} testid="home-features" />
        </div>
      </BlockCard>

      <BlockCard title="Статистика (счётчики из БД)" enabled={stats.enabled !== false} onToggle={(v) => setBlock("stats", { enabled: v })} testid="home-stats">
        <div className="space-y-1.5">
          <Label>Заголовок блока</Label>
          <Input value={stats.title || ""} onChange={(e) => setBlock("stats", { title: e.target.value })} className="border-neutral-300" data-testid="home-stats-title" />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {[
            ["show_acts", "label_acts", "Акты"],
            ["show_brands", "label_brands", "Марки"],
            ["show_verifications", "label_verifications", "Проверки"],
          ].map(([showKey, labelKey, name]) => (
            <div key={showKey} className="border border-neutral-200 rounded-md p-3 space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">{name}</span>
                <Switch checked={stats[showKey] !== false} onCheckedChange={(v) => setBlock("stats", { [showKey]: v })} data-testid={`home-stats-${showKey}`} />
              </div>
              <Input value={stats[labelKey] || ""} onChange={(e) => setBlock("stats", { [labelKey]: e.target.value })} placeholder="Подпись" className="border-neutral-300 text-sm" data-testid={`home-stats-${labelKey}`} />
            </div>
          ))}
        </div>
      </BlockCard>
    </div>
  );
}
